import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { loadAnalyticsConfig, GoogleAnalyticsConfig } from '../utils/analyticsConfig';

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Initialize Google Analytics with configuration from JSON file
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();
  const [gaConfig, setGaConfig] = useState<GoogleAnalyticsConfig | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load configuration and initialize GA
  useEffect(() => {
    const initializeGA = async () => {
      const config = await loadAnalyticsConfig();
      
      if (!config || !config.googleAnalytics.enabled) {
        console.log('Google Analytics is disabled');
        return;
      }

      const { measurementId, debug } = config.googleAnalytics;
      
      if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
        console.warn('Google Analytics measurement ID not configured');
        return;
      }

      setGaConfig(config.googleAnalytics);

      // Initialize Google Analytics script
      if (!window.gtag) {
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script1);

        const script2 = document.createElement('script');
        script2.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', ${JSON.stringify(config.googleAnalytics.config)});
        `;
        document.head.appendChild(script2);

        if (debug) {
          console.log('Google Analytics initialized with ID:', measurementId);
        }
      }

      setIsInitialized(true);
    };

    initializeGA();
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!isInitialized || !gaConfig) return;

    const { measurementId, debug } = gaConfig;

    if (window.gtag) {
      window.gtag('config', measurementId, {
        page_path: location.pathname + location.search,
      });

      if (debug) {
        console.log('Page view tracked:', location.pathname + location.search);
      }
    }
  }, [location, isInitialized, gaConfig]);

  return { isInitialized, gaConfig };
};

/**
 * Track custom events
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track custom page view
 */
export const trackPageView = (path: string, title?: string) => {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};
